import React, { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Box, Button, FormControlLabel, IconButton, MenuItem, Select, Switch, Typography } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import type { IContest, TContestPhase } from '../../../types/global/helper';
import { CustomizedMultipleInput } from '../../../components/CustomizedMultipleInput/CustomizedMultipleInput';
import { ContestPhaseSelector } from '../../../components/EsForm/ContestPhaseSelector';
import type { PhaseData } from '../../../components/EsForm/ContestPhaseTab';
import { downloadProject } from '../../../redux/project/actions';
import { useDispatchPromise } from '../../../utils/AppUtils';
import { phaseOptionsForCampaign, formatPhaseLabel } from '../contestWizard/phaseConfig';
import { DrawerSectionEditorActions, editTriggerSx, sectionShellSx } from './BrandContestDetailsDrawerUi';
import {
    BrandDrawerEmptyMessage,
    brandChipSx,
    dashedFieldShellSx,
    FieldHint,
    getAttachmentFileName,
    getAttachmentIcon,
} from './brandContestDrawerFieldUtils';
import {
    editorialHeadlineSx,
    editorialLabelSx,
    editorialSelectMenuProps,
    midnight,
} from '../contestWizard/midnightStyles';

type EditSection = 'campaign_phases' | 'contest_reference' | 'playlist' | 'contest_media' | 'preselection' | null;

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const PRESELECTION_LOCKED_MESSAGE = 'Campaign already has submissions. You cannot change the pre-selection settings.';

export interface BrandContestSetupDrawerBodyProps {
    contest: IContest;
    onSaveChanges: (payload: Record<string, unknown>) => void;
    hasSubmissions?: boolean;
    saving?: boolean;
}

function contestPhasesToPhaseData(contest: IContest): PhaseData[] {
    return (contest.contest_phase_dates ?? []).map(p => ({
        phase: p.phase,
        date_started: p.date_started,
        expiration_date: p.expiration_date,
    }));
}

function resolvePhasesEnabled(contest: IContest): boolean {
    if (contest.contest_phases_enabled != null) return Boolean(contest.contest_phases_enabled);
    return (contest.contest_phase_dates?.length ?? 0) > 0;
}

function defaultPhaseRow(contest: IContest): PhaseData {
    const options = phaseOptionsForCampaign(
        contest.submission_state === 'private_submission' ? 'private_submission' : 'public_submission',
    );
    return {
        phase: (options[0] || 'submission') as PhaseData['phase'],
        date_started: '',
        expiration_date: '',
    };
}

function validateReferenceIds(field: 'contest_reference' | 'playlist', values: string[]): string | null {
    if (field === 'contest_reference') {
        const invalid = values.filter(v => v && !v.startsWith('PR-'));
        if (invalid.length) {
            return 'All references must start with "PR-". Ensure the video exists and is public.';
        }
    } else {
        const invalid = values.filter(v => v && !v.startsWith('PL-'));
        if (invalid.length) {
            return 'All playlists must start with "PL-". Ensure the playlist exists and is available.';
        }
    }
    return null;
}

const brandSwitchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: midnight.primary },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        bgcolor: 'rgba(255,201,101,0.25)',
    },
};

const phaseControlRowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    bgcolor: midnight.surfaceHighest,
    p: 2,
    borderRadius: '10px',
    gap: 2,
};

const brandPhaseSelectSx = {
    minWidth: { xs: 128, sm: 160 },
    flexShrink: 0,
    bgcolor: midnight.surfaceContainer,
    borderRadius: 2,
    fontWeight: 700,
    fontSize: 14,
    color: midnight.primary,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-icon': { color: midnight.primary },
};

function PhaseControlRow({ title, subtitle, control }: { title: string; subtitle: string; control: React.ReactNode }) {
    return (
        <Box sx={phaseControlRowSx}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 700, color: midnight.onSurface, fontSize: 14 }}>{title}</Typography>
                <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, mt: 0.25 }}>{subtitle}</Typography>
            </Box>
            {control}
        </Box>
    );
}

function resolveActivePhaseOptions(contest: IContest, scheduledPhases: PhaseData[]): TContestPhase[] {
    const base = phaseOptionsForCampaign(
        contest.submission_state === 'private_submission' ? 'private_submission' : 'public_submission',
    ).filter(p => p !== 'closed') as TContestPhase[];
    if (scheduledPhases.length === 0) return base;
    const scheduled = new Set<string>(scheduledPhases.map(p => p.phase));
    const fromSchedule = base.filter(p => scheduled.has(p));
    return fromSchedule.length > 0 ? fromSchedule : base;
}

export function BrandContestSetupDrawerBody({
    contest,
    onSaveChanges,
    hasSubmissions = false,
    saving,
}: BrandContestSetupDrawerBodyProps) {
    const dispatchPromise = useDispatchPromise();
    const [editing, setEditing] = useState<EditSection>(null);
    const [draftReferences, setDraftReferences] = useState<string[]>(contest.contest_reference ?? []);
    const [draftPlaylist, setDraftPlaylist] = useState<string[]>(contest.playlist ?? []);
    const [draftMediaUrls, setDraftMediaUrls] = useState<string[]>([]);
    const [newAttachmentFiles, setNewAttachmentFiles] = useState<File[]>([]);
    const [validationError, setValidationError] = useState<string | null>(null);

    const [draftPhasesEnabled, setDraftPhasesEnabled] = useState(() => resolvePhasesEnabled(contest));
    const [draftPhaseDates, setDraftPhaseDates] = useState<PhaseData[]>(() => contestPhasesToPhaseData(contest));
    const [phaseValidationError, setPhaseValidationError] = useState<string | null>(null);

    const [draftPreselectionEnabled, setDraftPreselectionEnabled] = useState(() =>
        Boolean(contest.preselection_enabled),
    );

    const currentContestPhase = (contest.contest_phase || 'submission') as TContestPhase;
    const isCampaignClosed = currentContestPhase === 'closed';
    const [manualActivePhase, setManualActivePhase] = useState<TContestPhase>(() =>
        isCampaignClosed ? 'submission' : currentContestPhase,
    );

    const phasesEnabled = resolvePhasesEnabled(contest);
    const displayPhaseDates = contestPhasesToPhaseData(contest);
    const preselectionLocked = hasSubmissions;

    const activePhaseOptions = useMemo(
        () => resolveActivePhaseOptions(contest, displayPhaseDates),
        [contest, displayPhaseDates],
    );

    useEffect(() => {
        if (contest.contest_phase && contest.contest_phase !== 'closed') {
            setManualActivePhase(contest.contest_phase as TContestPhase);
        }
    }, [contest.contest_phase]);

    useEffect(() => {
        if (editing !== null) return;
        setDraftReferences(contest.contest_reference ?? []);
        setDraftPlaylist(contest.playlist ?? []);
        setDraftPhasesEnabled(resolvePhasesEnabled(contest));
        setDraftPhaseDates(contestPhasesToPhaseData(contest));
        setDraftPreselectionEnabled(Boolean(contest.preselection_enabled));
        setPhaseValidationError(null);
    }, [contest, editing]);

    const beginEdit = (section: Exclude<EditSection, null>) => {
        if (section === 'preselection' && preselectionLocked) return;
        setValidationError(null);
        setPhaseValidationError(null);
        if (section === 'contest_reference') {
            setDraftReferences(contest.contest_reference ?? []);
        } else if (section === 'playlist') {
            setDraftPlaylist(contest.playlist ?? []);
        } else if (section === 'contest_media') {
            const urls = (contest.contest_media ?? []).filter((item): item is string => typeof item === 'string');
            setDraftMediaUrls(urls);
            setNewAttachmentFiles([]);
        } else if (section === 'campaign_phases') {
            setDraftPhasesEnabled(resolvePhasesEnabled(contest));
            setDraftPhaseDates(contestPhasesToPhaseData(contest));
        } else if (section === 'preselection') {
            setDraftPreselectionEnabled(Boolean(contest.preselection_enabled));
        }
        setEditing(section);
    };

    const cancelEdit = () => {
        setValidationError(null);
        setPhaseValidationError(null);
        setNewAttachmentFiles([]);
        setEditing(null);
    };

    const handlePhaseChange = (
        phase: { phase: string; date_started: string; expiration_date: string },
        replace?: boolean,
        index?: number,
    ) => {
        const updatedPhase: PhaseData = {
            phase: phase.phase as PhaseData['phase'],
            date_started: phase.date_started,
            expiration_date: phase.expiration_date,
        };
        if (replace && index !== undefined) {
            const next = [...draftPhaseDates];
            next[index] = updatedPhase;
            setDraftPhaseDates(next);
        } else {
            setDraftPhaseDates([...draftPhaseDates, updatedPhase]);
        }
    };

    const handlePhaseRemove = (_phase: PhaseData, index?: number) => {
        if (index === undefined) return;
        setDraftPhaseDates(draftPhaseDates.filter((_, i) => i !== index));
    };

    const toggleDraftPhases = (enabled: boolean) => {
        setDraftPhasesEnabled(enabled);
        if (enabled && draftPhaseDates.length === 0) {
            setDraftPhaseDates([defaultPhaseRow(contest)]);
        }
    };

    const handleActivePhaseChange = (phase: TContestPhase) => {
        setManualActivePhase(phase);
        if (!isCampaignClosed && phase !== currentContestPhase) {
            onSaveChanges({ contest_phase: phase });
        }
    };

    const handleCloseCampaignToggle = (closed: boolean) => {
        if (closed) {
            if (currentContestPhase !== 'closed') {
                onSaveChanges({ contest_phase: 'closed' });
            }
            return;
        }
        const restore = activePhaseOptions.includes(manualActivePhase)
            ? manualActivePhase
            : activePhaseOptions[0] ?? 'submission';
        setManualActivePhase(restore);
        onSaveChanges({ contest_phase: restore });
    };

    const renderPhaseAdminControls = (scheduledPhases: PhaseData[]) => {
        const options = resolveActivePhaseOptions(contest, scheduledPhases);
        const selectValue = options.includes(manualActivePhase) ? manualActivePhase : options[0] ?? 'submission';

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                <PhaseControlRow
                    title="Active phase"
                    subtitle="Set which phase the campaign is in right now."
                    control={
                        <Select
                            size="small"
                            value={selectValue}
                            disabled={isCampaignClosed || saving}
                            onChange={e => handleActivePhaseChange(e.target.value as TContestPhase)}
                            IconComponent={ExpandMore}
                            MenuProps={editorialSelectMenuProps}
                            sx={brandPhaseSelectSx}>
                            {options.map(phase => (
                                <MenuItem key={phase} value={phase}>
                                    {formatPhaseLabel(phase)}
                                </MenuItem>
                            ))}
                        </Select>
                    }
                />
                <PhaseControlRow
                    title="Close campaign"
                    subtitle="Mark the campaign closed — no new activity until reopened."
                    control={
                        <Switch
                            checked={isCampaignClosed}
                            disabled={saving}
                            onChange={e => handleCloseCampaignToggle(e.target.checked)}
                            sx={brandSwitchSx}
                        />
                    }
                />
            </Box>
        );
    };

    const saveCampaignPhases = useCallback(() => {
        if (phaseValidationError) return;
        const nextDates = draftPhasesEnabled
            ? draftPhaseDates.map(({ phase, date_started, expiration_date }) => ({
                  phase,
                  date_started,
                  expiration_date,
              }))
            : [];
        const curEnabled = resolvePhasesEnabled(contest);
        const curDates = contest.contest_phase_dates ?? [];
        const datesChanged =
            nextDates.length !== curDates.length ||
            nextDates.some((row, i) => {
                const cur = curDates[i];
                return (
                    !cur ||
                    row.phase !== cur.phase ||
                    row.date_started !== cur.date_started ||
                    row.expiration_date !== cur.expiration_date
                );
            });
        const shouldSavePhases = draftPhasesEnabled || draftPhasesEnabled !== curEnabled || datesChanged;
        if (shouldSavePhases) {
            onSaveChanges({
                contest_phases_enabled: draftPhasesEnabled,
                contest_phase_dates: nextDates,
            });
        }
        setEditing(null);
        setPhaseValidationError(null);
    }, [contest, draftPhaseDates, draftPhasesEnabled, onSaveChanges, phaseValidationError]);

    const savePreselection = useCallback(() => {
        const cur = Boolean(contest.preselection_enabled);
        if (draftPreselectionEnabled !== cur) {
            onSaveChanges({ preselection_enabled: draftPreselectionEnabled });
        }
        setEditing(null);
    }, [contest.preselection_enabled, draftPreselectionEnabled, onSaveChanges]);

    const saveReferences = useCallback(() => {
        const err = validateReferenceIds('contest_reference', draftReferences);
        if (err) {
            setValidationError(err);
            return;
        }
        onSaveChanges({ contest_reference: draftReferences });
        setEditing(null);
        setValidationError(null);
    }, [draftReferences, onSaveChanges]);

    const savePlaylist = useCallback(() => {
        const err = validateReferenceIds('playlist', draftPlaylist);
        if (err) {
            setValidationError(err);
            return;
        }
        onSaveChanges({ playlist: draftPlaylist });
        setEditing(null);
        setValidationError(null);
    }, [draftPlaylist, onSaveChanges]);

    const saveAttachments = useCallback(() => {
        if (validationError) return;
        const allMedia = newAttachmentFiles.length > 0 ? [...draftMediaUrls, ...newAttachmentFiles] : draftMediaUrls;
        onSaveChanges({ contest_media: allMedia });
        setNewAttachmentFiles([]);
        setEditing(null);
        setValidationError(null);
    }, [draftMediaUrls, newAttachmentFiles, onSaveChanges, validationError]);

    const handleDownloadAttachment = async (attachmentUrl: string) => {
        try {
            const response: unknown = await dispatchPromise(downloadProject(attachmentUrl));
            const blob = await (response as Response).blob();
            const anchor = document.createElement('a');
            anchor.href = window.URL.createObjectURL(blob);
            anchor.download = getAttachmentFileName(attachmentUrl);
            anchor.click();
            window.URL.revokeObjectURL(anchor.href);
        } catch {
            /* globalErrorHandler may surface */
        }
    };

    const handleAttachmentFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(event.target.files || []);
        const invalid: string[] = [];
        const valid: File[] = [];
        selected.forEach(file => {
            const name = file.name.toLowerCase();
            const okType =
                file.type === 'application/pdf' ||
                file.type === 'application/zip' ||
                name.endsWith('.pdf') ||
                name.endsWith('.zip');
            if (!okType) {
                invalid.push(`${file.name} — only PDF and ZIP are supported`);
            } else if (file.size > MAX_ATTACHMENT_BYTES) {
                invalid.push(`${file.name} — exceeds 10MB limit`);
            } else {
                valid.push(file);
            }
        });
        if (invalid.length) {
            setValidationError(invalid.join('\n'));
            return;
        }
        setValidationError(null);
        if (valid.length) setNewAttachmentFiles(prev => [...prev, ...valid]);
        event.target.value = '';
    };

    const sectionHeader = (
        title: string,
        section: Exclude<EditSection, null>,
        locked?: { disabled: boolean; message?: string },
    ) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 2 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 20, color: midnight.onSurface }}>{title}</Typography>
            {editing === section ? (
                <Box />
            ) : (
                <Button
                    type="button"
                    variant="text"
                    size="small"
                    disabled={locked?.disabled}
                    onClick={() => beginEdit(section)}
                    sx={editTriggerSx}
                    title={locked?.message}>
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    Edit
                </Button>
            )}
        </Box>
    );

    const multiInputStyle = {
        backgroundColor: midnight.surfaceHighest,
        borderRadius: '10px',
    };

    const mediaUrls = (contest.contest_media ?? []).filter((item): item is string => typeof item === 'string');
    const disableVotingPhase = contest.submission_state === 'private_submission';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={sectionShellSx}>
                {sectionHeader('Campaign phases', 'campaign_phases')}
                <FieldHint>Set up the different phases of your campaign.</FieldHint>
                {editing === 'campaign_phases' ? (
                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                bgcolor: midnight.surfaceHighest,
                                p: 2,
                                borderRadius: '10px',
                                mb: 2,
                            }}>
                            <Box>
                                <Typography sx={{ fontWeight: 700, color: midnight.onSurface, fontSize: 14 }}>
                                    Enable campaign phases
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, mt: 0.25 }}>
                                    Schedule submission, judging, voting, and more.
                                </Typography>
                            </Box>
                            <Switch
                                checked={draftPhasesEnabled}
                                onChange={e => toggleDraftPhases(e.target.checked)}
                                sx={brandSwitchSx}
                            />
                        </Box>
                        {draftPhasesEnabled ? (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '10px',
                                    bgcolor: midnight.surfaceHighest,
                                    border: '1px solid rgba(72,72,71,0.35)',
                                }}>
                                <ContestPhaseSelector
                                    phases={draftPhaseDates}
                                    onChange={handlePhaseChange}
                                    onRemovePhase={handlePhaseRemove}
                                    onUpdatePhase={(arg, index) => handlePhaseChange(arg, true, index)}
                                    onValidationError={setPhaseValidationError}
                                    disableVotingPhase={disableVotingPhase}
                                    visualVariant="brand"
                                />
                            </Box>
                        ) : (
                            <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 13, fontStyle: 'italic' }}>
                                Campaign phases disabled — save to clear scheduled phases.
                            </Typography>
                        )}
                        {phaseValidationError ? (
                            <Typography sx={{ color: '#ff7351', fontSize: 12, mt: 1 }}>
                                {phaseValidationError}
                            </Typography>
                        ) : null}
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={saveCampaignPhases}
                            saving={saving}
                            saveDisabled={Boolean(phaseValidationError)}
                        />
                        {renderPhaseAdminControls(draftPhaseDates)}
                    </Box>
                ) : (
                    <Box>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => beginEdit('campaign_phases')}
                            sx={{ ...dashedFieldShellSx, width: '100%', textAlign: 'left' }}>
                            {phasesEnabled && displayPhaseDates.length > 0 ? (
                                <ContestPhaseSelector
                                    phases={displayPhaseDates}
                                    onChange={() => undefined}
                                    onRemovePhase={() => undefined}
                                    readOnly
                                    visualVariant="brand"
                                />
                            ) : (
                                <BrandDrawerEmptyMessage message="Campaign phases disabled" />
                            )}
                        </Box>
                        {renderPhaseAdminControls(displayPhaseDates)}
                    </Box>
                )}
            </Box>

            <Box sx={sectionShellSx}>
                {sectionHeader('Pre-selection settings', 'preselection', {
                    disabled: preselectionLocked,
                    message: PRESELECTION_LOCKED_MESSAGE,
                })}
                <FieldHint>Enable pre-selection to review submissions before assigning to jurors.</FieldHint>
                {preselectionLocked && (
                    <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 12, mb: 2, fontStyle: 'italic' }}>
                        {PRESELECTION_LOCKED_MESSAGE}
                    </Typography>
                )}
                {editing === 'preselection' ? (
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={draftPreselectionEnabled}
                                    onChange={e => setDraftPreselectionEnabled(e.target.checked)}
                                    sx={brandSwitchSx}
                                />
                            }
                            label={
                                <Typography sx={{ fontWeight: 600, color: midnight.onSurface, fontSize: 14 }}>
                                    Enable pre-selection
                                </Typography>
                            }
                            sx={{ ml: 0, mb: 2 }}
                        />
                        <DrawerSectionEditorActions onCancel={cancelEdit} onSave={savePreselection} saving={saving} />
                    </Box>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        disabled={preselectionLocked}
                        onClick={preselectionLocked ? undefined : () => beginEdit('preselection')}
                        sx={{
                            ...dashedFieldShellSx,
                            width: '100%',
                            textAlign: 'left',
                            cursor: preselectionLocked ? 'not-allowed' : 'pointer',
                            opacity: preselectionLocked ? 0.6 : 1,
                        }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 15, color: midnight.onSurface }}>
                            {contest.preselection_enabled ? 'Enabled' : 'Disabled'}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={sectionShellSx}>
                {sectionHeader('Campaign Reference', 'contest_reference')}
                <FieldHint>Paste a video ID that participants can follow (usually starts with PR-).</FieldHint>
                {editing === 'contest_reference' ? (
                    <Box>
                        <CustomizedMultipleInput
                            key={`refs-edit-${contest._id}-${draftReferences.join('|')}`}
                            brandForm
                            autoCompleteOptions={[]}
                            initialValue={draftReferences}
                            onChange={(refs: string[]) => {
                                setDraftReferences(refs);
                                setValidationError(validateReferenceIds('contest_reference', refs));
                            }}
                            style={multiInputStyle}
                            entyLimit={3}
                        />
                        {validationError && editing === 'contest_reference' && (
                            <Typography sx={{ color: '#ff7351', fontSize: 12, mt: 1 }}>{validationError}</Typography>
                        )}
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={saveReferences}
                            saving={saving}
                            saveDisabled={Boolean(validationError)}
                        />
                    </Box>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => beginEdit('contest_reference')}
                        sx={{ ...dashedFieldShellSx, width: '100%', textAlign: 'left' }}>
                        {(contest.contest_reference?.length ?? 0) > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {contest.contest_reference!.map((ref, i) => (
                                    <Box key={`${ref}-${i}`} component="span" sx={brandChipSx}>
                                        {ref}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <BrandDrawerEmptyMessage message="No campaign references provided" />
                        )}
                    </Box>
                )}
            </Box>

            <Box sx={sectionShellSx}>
                {sectionHeader('Campaign Playlist', 'playlist')}
                <FieldHint>
                    Paste a playlist ID. Submissions for this campaign appear on that playlist (usually starts with
                    PL-).
                </FieldHint>
                {editing === 'playlist' ? (
                    <Box>
                        <CustomizedMultipleInput
                            key={`playlist-edit-${contest._id}-${draftPlaylist.join('|')}`}
                            brandForm
                            autoCompleteOptions={[]}
                            initialValue={draftPlaylist}
                            onChange={(items: string[]) => {
                                setDraftPlaylist(items);
                                setValidationError(validateReferenceIds('playlist', items));
                            }}
                            style={multiInputStyle}
                            entyLimit={3}
                        />
                        {validationError && editing === 'playlist' && (
                            <Typography sx={{ color: '#ff7351', fontSize: 12, mt: 1 }}>{validationError}</Typography>
                        )}
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={savePlaylist}
                            saving={saving}
                            saveDisabled={Boolean(validationError)}
                        />
                    </Box>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => beginEdit('playlist')}
                        sx={{ ...dashedFieldShellSx, width: '100%', textAlign: 'left' }}>
                        {(contest.playlist?.length ?? 0) > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {contest.playlist!.map((pl, i) => (
                                    <Box key={`${pl}-${i}`} component="span" sx={brandChipSx}>
                                        {pl}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <BrandDrawerEmptyMessage message="No campaign playlists provided" />
                        )}
                    </Box>
                )}
            </Box>

            <Box sx={sectionShellSx}>
                {sectionHeader('Campaign Attachments', 'contest_media')}
                <FieldHint>Upload PDF or ZIP files (max 10MB per file) that participants can download.</FieldHint>
                {editing === 'contest_media' ? (
                    <Box>
                        {draftMediaUrls.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography sx={{ ...editorialLabelSx, mb: 1 }}>Existing attachments</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {draftMediaUrls.map((url, index) => (
                                        <Box
                                            key={`${url}-${index}`}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 1.5,
                                                borderRadius: '10px',
                                                bgcolor: midnight.surfaceHighest,
                                                border: '1px solid rgba(72,72,71,0.35)',
                                            }}>
                                            {getAttachmentIcon(url)}
                                            <Typography
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    fontSize: 13,
                                                    color: midnight.onSurface,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                {getAttachmentFileName(url)}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                aria-label="Download"
                                                onClick={() => handleDownloadAttachment(url)}
                                                sx={{ color: midnight.onSurfaceVariant }}>
                                                <DownloadOutlinedIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label="Remove"
                                                onClick={() =>
                                                    setDraftMediaUrls(draftMediaUrls.filter((_, i) => i !== index))
                                                }
                                                sx={{
                                                    color: midnight.onSurfaceVariant,
                                                    '&:hover': { color: '#ff7351' },
                                                }}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {newAttachmentFiles.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography sx={{ ...editorialLabelSx, mb: 1 }}>New attachments</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {newAttachmentFiles.map((file, index) => (
                                        <Box
                                            key={`${file.name}-${index}`}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 1.5,
                                                borderRadius: '10px',
                                                bgcolor: midnight.surfaceHighest,
                                                border: '1px solid rgba(72,72,71,0.35)',
                                            }}>
                                            {file.name.toLowerCase().endsWith('.pdf') ? (
                                                <PictureAsPdfOutlinedIcon sx={{ fontSize: 28, color: '#ff7351' }} />
                                            ) : (
                                                <FolderZipOutlinedIcon sx={{ fontSize: 28, color: midnight.primary }} />
                                            )}
                                            <Typography sx={{ flex: 1, fontSize: 13, color: midnight.onSurface }}>
                                                {file.name}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                aria-label="Remove new file"
                                                onClick={() =>
                                                    setNewAttachmentFiles(
                                                        newAttachmentFiles.filter((_, i) => i !== index),
                                                    )
                                                }
                                                sx={{
                                                    color: midnight.onSurfaceVariant,
                                                    '&:hover': { color: '#ff7351' },
                                                }}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <input
                            type="file"
                            accept=".pdf,.zip"
                            multiple
                            style={{ display: 'none' }}
                            id="brand-contest-attachment-upload"
                            onChange={handleAttachmentFileChange}
                        />
                        <label htmlFor="brand-contest-attachment-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<AttachFileOutlinedIcon />}
                                sx={{
                                    borderRadius: 999,
                                    borderColor: 'rgba(255,201,101,0.45)',
                                    color: midnight.primary,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    '&:hover': {
                                        borderColor: midnight.primary,
                                        bgcolor: 'rgba(255,201,101,0.08)',
                                    },
                                }}>
                                Add attachments
                            </Button>
                        </label>
                        {validationError && editing === 'contest_media' && (
                            <Typography sx={{ color: '#ff7351', fontSize: 12, mt: 1, whiteSpace: 'pre-line' }}>
                                {validationError}
                            </Typography>
                        )}
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={saveAttachments}
                            saving={saving}
                            saveDisabled={Boolean(validationError)}
                        />
                    </Box>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => beginEdit('contest_media')}
                        sx={{ ...dashedFieldShellSx, width: '100%', textAlign: 'left' }}>
                        {mediaUrls.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {mediaUrls.map((url, index) => (
                                    <Box
                                        key={`${url}-${index}`}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                                        onClick={e => e.stopPropagation()}>
                                        {getAttachmentIcon(url)}
                                        <Typography sx={{ fontSize: 13, color: midnight.onSurface }}>
                                            {getAttachmentFileName(url)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <BrandDrawerEmptyMessage message="No attachments provided" />
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
